import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { type BriefRow, buildBriefGroups } from './briefCards';
import { groupBriefBlocks, parseBrief } from './briefMarkdown';

/**
 * The brief, drawn.
 *
 * Blocks are turned into real elements rather than one dangerouslySet blob:
 * the outer structure is React's, and only the INLINE html of each block is
 * injected. That keeps the shape of the document out of the injected string,
 * so a malformed paste cannot close a tag that was never opened and swallow
 * the rest of the page.
 *
 * DOMPurify runs anyway, on every inline fragment. briefMarkdown escapes
 * first and only reintroduces a fixed set of marks, so this is the second of
 * two doors rather than the only one.
 */

const INLINE_TAGS = ['strong', 'em', 'code', 'a'];
const INLINE_ATTRS = ['href', 'target', 'rel'];

function clean(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: INLINE_TAGS,
    ALLOWED_ATTR: INLINE_ATTRS,
  });
}

export function BriefBody({ markdown }: { markdown: string }) {
  const groups = useMemo(
    () => buildBriefGroups(groupBriefBlocks(parseBrief(markdown))),
    [markdown],
  );

  return (
    <article className="brief-body">
      {groups.map((group, groupIndex) => {
        const key = `g-${String(groupIndex)}`;
        return (
          <section key={key} className={`brief-group${group.tone === 'needs' ? ' is-needs' : ''}`}>
            {group.label === null ? null : (
              <h2
                className="brief-group-label"
                dangerouslySetInnerHTML={{ __html: clean(group.label) }}
              />
            )}
            {group.cards.map((card, cardIndex) => {
              const cardKey = `${key}-c${String(cardIndex)}`;
              return (
                <div key={cardKey} className="brief-card">
                  {card.title === null ? null : (
                    <div className="brief-card-head">
                      <h3
                        className="brief-card-title"
                        dangerouslySetInnerHTML={{ __html: clean(card.title) }}
                      />
                      {card.status === null ? null : (
                        <span className={`brief-status is-${card.status}`}>
                          {card.status === 'clear' ? 'Nothing for Alex' : 'Needs Alex'}
                        </span>
                      )}
                    </div>
                  )}
                  {card.rows.map((row, rowIndex) =>
                    renderRow(row, `${cardKey}-r${String(rowIndex)}`),
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </article>
  );
}

function renderRow(row: BriefRow, key: string) {
  const List = row.numbered ? 'ol' : 'ul';
  return (
    <div key={key} className="brief-row">
      {row.label === null ? null : (
        <p className="brief-row-label" dangerouslySetInnerHTML={{ __html: clean(row.label) }} />
      )}
      {row.html === null ? null : (
        <p className="brief-row-text" dangerouslySetInnerHTML={{ __html: clean(row.html) }} />
      )}
      {row.bullets.length === 0 ? null : (
        <List className="brief-list">
          {row.bullets.map((item, index) => (
            <li key={`${key}-${String(index)}`} dangerouslySetInnerHTML={{ __html: clean(item) }} />
          ))}
        </List>
      )}
    </div>
  );
}
